/**
 * Canonical lead / registration sources — keep in sync with backend `constants/lead-sources.ts`.
 * Used by patient registration, OPD booking, IPD admission, and CRM lead pickers.
 */
export const CANONICAL_LEAD_SOURCES: Array<{ value: string; label: string }> = [
	{ value: 'WALK_IN', label: 'Walk-in' },
	{ value: 'REFERRAL', label: 'Referral' },
	{ value: 'WEBSITE', label: 'Website' },
	{ value: 'DIRECT_LANDING', label: 'Direct landing' },
	{ value: 'GOOGLE', label: 'Google' },
	{ value: 'GOOGLE_PAID', label: 'Google Paid' },
	{ value: 'ORGANIC_GOOGLE', label: 'Organic Google' },
	{ value: 'GMB', label: 'Google My Business' },
	{ value: 'META', label: 'Meta' },
	{ value: 'META_PAID', label: 'Meta Paid' },
	{ value: 'WHATSAPP', label: 'WhatsApp' },
	{ value: 'WHATSAPP_INSTA_ADS', label: 'WhatsApp / Insta Ads' },
	{ value: 'INSTAGRAM_ORGANIC', label: 'Instagram Organic' },
	{ value: 'ORGANIC_DIRECT', label: 'Organic / direct (WhatsApp)' },
	{ value: 'PRACTO', label: 'Practo' },
	{ value: 'HEXA', label: 'Hexa' },
	{ value: 'OFFLINE_MARKETING', label: 'Offline Marketing' },
	{ value: 'OFFLINE', label: 'Offline' },
	{ value: 'EXOTEL', label: 'Exotel' },
	{ value: 'AFFILIATE', label: 'Affiliate' },
	{ value: 'DIGITAL', label: 'Digital (Legacy)' },
	{ value: 'CUSTOM', label: 'Custom' },
	{ value: 'OTHER', label: 'Other' },
];

/** @deprecated Use CANONICAL_LEAD_SOURCES — kept for existing imports. */
export const REGISTRATION_SOURCES = CANONICAL_LEAD_SOURCES;

/** @deprecated Use CANONICAL_LEAD_SOURCES — kept for CRM / IPD helpers. */
export const CRM_LEAD_SOURCES = CANONICAL_LEAD_SOURCES;

const LABEL_BY_VALUE = new Map(CANONICAL_LEAD_SOURCES.map((s) => [s.value, s.label] as const));

export function normalizeLeadSourceKey(raw: unknown): string {
	return String(raw ?? '')
		.trim()
		.toUpperCase()
		.replace(/[\s-]+/g, '_')
		.replace(/_+/g, '_');
}

export function formatLeadSourceLabel(value: string, details?: string): string {
	const raw = String(value ?? '').trim();
	if (raw.startsWith('DOCTOR_LEAD:')) {
		const d = String(details ?? '').trim();
		return d || 'Doctor Lead';
	}
	const key = normalizeLeadSourceKey(value);
	return LABEL_BY_VALUE.get(key) ?? key.replace(/_/g, ' ');
}

/** Build sorted picklist; merges stored/API values not in the canonical list. */
export function buildLeadSourceOptions(apiSources?: string[]): Array<{ value: string; label: string }> {
	const map = new Map<string, string>();
	for (const s of CANONICAL_LEAD_SOURCES) {
		map.set(s.value, s.label);
	}
	for (const raw of apiSources ?? []) {
		const rawStr = String(raw ?? '').trim();
		if (rawStr.startsWith('DOCTOR_LEAD:')) {
			if (!map.has(rawStr)) map.set(rawStr, formatLeadSourceLabel(rawStr));
			continue;
		}
		const v = normalizeLeadSourceKey(raw);
		if (v && !map.has(v)) {
			map.set(v, formatLeadSourceLabel(v));
		}
	}
	return Array.from(map.entries())
		.map(([value, label]) => ({ value, label }))
		.sort((a, b) => a.label.localeCompare(b.label));
}

/** Options for SearchableSelect — label, value key, and combined search text. */
export function toLeadSourceSearchableOptions(
	sources: Array<{ value: string; label: string }>
): Array<{ value: string; label: string; searchText: string }> {
	return sources.map((s) => ({
		value: s.value,
		label: s.label,
		searchText: [s.label, s.value.replace(/_/g, ' '), s.value].join(' '),
	}));
}

/** @deprecated Use buildLeadSourceOptions */
export const buildIpdLeadSourceOptions = buildLeadSourceOptions;

export function leadSourceNeedsDetails(value: string | null | undefined): boolean {
	const v = normalizeLeadSourceKey(value);
	return v === 'REFERRAL' || v === 'AFFILIATE' || v === 'CUSTOM' || v === 'OTHER';
}
